import { useEffect, useState, useSyncExternalStore } from "react";
import { CircleMarker, Tooltip } from "react-leaflet";
import { fetchStationInventory, type AemetStation } from "../../model/aemet/aemet.ts";
import { aemetJobStore } from "../../model/aemet/aemetJobStore.ts";
import {
  AEMET_BACKUP_STATION_STYLE,
  AEMET_MAIN_STATION_STYLE,
  AEMET_STATION_STYLE,
} from "../mapStyles.ts";

/**
 * All of AEMET's ~920 weather stations, as small background dots, shown
 * only while the AEMET fetch dialog is actually open (aemetJobStore.ts's
 * openDialogSubBasinCode) — NOT for every job tracked in the background.
 * Closing the dialog (even mid-download, the job itself keeps running)
 * clears these markers too: a job left running in the background for a
 * while shouldn't leave the map cluttered with its stations for the rest
 * of the session, and AemetFetchDialog.tsx already tells the user the
 * download continues without needing this as a visual reminder. Its main
 * candidate station is highlighted in one color, its backups in another,
 * so their real position relative to the whole network is visible on the
 * map instead of only as text (name + distance) in the dialog.
 *
 * Cheap: `fetchStationInventory()` is fetched once and cached (same promise
 * AemetFetchDialog.tsx itself uses), and ~920 plain `CircleMarker`s with no
 * per-marker React state or icons is a trivial load for Leaflet — only paid
 * for while the dialog is actually open, never otherwise.
 *
 * Real bug found live: this used to render for ANY open job regardless of
 * `method` — but a "rocio" job's `candidates` are grid cells, not AEMET
 * stations (see `hasCoverage` in AemetBulkFetchDialog.tsx for the same
 * station-vs-grid distinction elsewhere), so main/backup highlighting never
 * matched anything real and the ~920 background station dots showed up on
 * the map while downloading from the grid, with no station actually in use.
 * Gated on `job.method !== "rocio"` below.
 */
export function AemetStationsLayer() {
  const openSubBasinCode = useSyncExternalStore(
    aemetJobStore.subscribe,
    aemetJobStore.getOpenDialogSubBasinCode
  );
  const openJob = openSubBasinCode != null ? aemetJobStore.getJob(openSubBasinCode) : undefined;
  const job = openJob?.method !== "rocio" ? openJob : undefined;
  const [stations, setStations] = useState<AemetStation[] | null>(null);
  const hasJob = job != null;

  useEffect(() => {
    if (!hasJob) return;
    let cancelled = false;
    fetchStationInventory().then((fetched) => {
      if (!cancelled) setStations(fetched);
    });
    return () => {
      cancelled = true;
    };
  }, [hasJob]);

  if (!job || !stations) return null;

  const mainCode = job.candidates[0]?.code;
  const backupCodes = new Set(job.candidates.slice(1).map((c) => c.code));

  const background: AemetStation[] = [];
  const backups: AemetStation[] = [];
  const mains: AemetStation[] = [];
  for (const s of stations) {
    if (s.code === mainCode) mains.push(s);
    else if (backupCodes.has(s.code)) backups.push(s);
    else background.push(s);
  }

  return (
    <>
      {background.map((s) => (
        <CircleMarker
          key={s.code}
          center={[s.lat, s.lon]}
          radius={2}
          pathOptions={AEMET_STATION_STYLE}
        />
      ))}
      {backups.map((s) => (
        <CircleMarker
          key={s.code}
          center={[s.lat, s.lon]}
          radius={6}
          pathOptions={AEMET_BACKUP_STATION_STYLE}
        >
          <Tooltip direction="top">{s.name}</Tooltip>
        </CircleMarker>
      ))}
      {mains.map((s) => (
        <CircleMarker
          key={s.code}
          center={[s.lat, s.lon]}
          radius={8}
          pathOptions={AEMET_MAIN_STATION_STYLE}
        >
          <Tooltip direction="top">{s.name}</Tooltip>
        </CircleMarker>
      ))}
    </>
  );
}
